// Fee & Obligation — Fees ledger panel (loan detail). Summary rollup + a
// table of the loan's fee rows. New fees are created through the 6-step
// AddObligationWizard (PR-2 — replaces the old Add Fee form); borrower-
// payable entries enter B3 review (pending_review), approval creates the
// WF1 obligation and the fee bills on the next statement. The inline Mark
// billed / Mark collected actions apply only to LEGACY pre-pipeline tracker
// rows (status "pending"); LoanFeeEntryModal survives for EDITS only. The
// "Add fee / obligation" button self-gates on Loans.Manage; everyone with
// Loans.Read sees the ledger.

import { useMemo, useState } from "react";
import { Plus } from "lucide-react";
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  EmptyState,
  Skeleton,
  StatusBadge,
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
  type StatusTone,
} from "@/design-system/components/ui";
import { formatCurrency, formatCalendarDate } from "@/design-system/lib/format";
import { useAuth } from "@/hooks/useAuth";
import {
  LOAN_FEE_STATUSES,
  PAYMENT_CHANNELS,
  type LoanFeeDto,
  type LoanFeeStatus,
} from "@/api/portfolioAdmin/loanFeesApi";
import { useLoanFees, useUpdateLoanFee } from "@/hooks/portfolioAdmin/useLoanFees";
import { AddObligationWizard } from "./AddObligationWizard";
import { LoanFeeEntryModal } from "./LoanFeeEntryModal";

const STATUS_TONE: Record<LoanFeeStatus, StatusTone> = {
  pending: "warn",
  pending_review: "warn",
  approved: "info",
  billed: "info",
  collected: "ok",
  waived: "muted",
  canceled: "muted",
};

const STATUS_LABEL: Record<LoanFeeStatus, string> = Object.fromEntries(
  LOAN_FEE_STATUSES.map((s) => [s.value, s.label]),
) as Record<LoanFeeStatus, string>;

const CHANNEL_LABEL: Record<number, string> = Object.fromEntries(
  PAYMENT_CHANNELS.map((c) => [c.value, c.label]),
);

export function LoanFeesPanel({ loanId }: { loanId: string }) {
  const { hasPermission } = useAuth();
  const canManage = hasPermission("Loans.Manage");

  const feesQuery = useLoanFees(loanId);
  const update = useUpdateLoanFee(loanId);

  const [wizardOpen, setWizardOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editing, setEditing] = useState<LoanFeeDto | undefined>(undefined);

  const fees = useMemo(() => feesQuery.data?.fees ?? [], [feesQuery.data]);
  const summary = feesQuery.data?.summary;

  const openAdd = () => setWizardOpen(true);
  const openEdit = (fee: LoanFeeDto) => {
    setEditing(fee);
    setEditModalOpen(true);
  };

  const setStatus = (fee: LoanFeeDto, status: LoanFeeStatus) =>
    update.mutate({ feeId: fee.id, body: { status } });

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-space-3">
          <div>
            <CardTitle>Fees</CardTitle>
            <CardDescription>
              Loan costs and fees. Borrower-payable entries go to B3 review;
              approved fees bill on the next statement and collect through the
              waterfall. Fees payable by others are recorded for tracking only.
            </CardDescription>
          </div>
          {canManage && (
            <Button size="sm" onClick={openAdd} data-testid="fees-add">
              <Plus size={16} aria-hidden /> Add fee / obligation
            </Button>
          )}
        </div>
      </CardHeader>

      {summary && (
        <>
          <div
            className="grid grid-cols-2 sm:grid-cols-4 gap-space-3 mb-space-1"
            data-testid="fees-summary"
          >
            <SummaryTile label="Total" value={formatCurrency(summary.totalAmount)} />
            <SummaryTile label="Unbilled" value={formatCurrency(summary.pendingAmount)} />
            <SummaryTile label="Billed" value={formatCurrency(summary.billedAmount)} />
            <SummaryTile label="Collected" value={formatCurrency(summary.collectedAmount)} />
          </div>
          <p className="text-caption text-gray-mid mb-space-3" data-testid="fees-total-note">
            &ldquo;Unbilled&rdquo; covers pending, in-review, and approved fees.
            &ldquo;Total&rdquo; counts every fee, including waived and canceled — it
            does not net to Unbilled + Billed + Collected.
          </p>
        </>
      )}

      {feesQuery.isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : feesQuery.isError ? (
        <p className="text-small text-status-error" role="alert" data-testid="fees-error">
          Couldn't load fees. Try again.
        </p>
      ) : fees.length === 0 ? (
        <EmptyState
          title="No fees recorded"
          description="No costs or fees have been entered for this loan yet."
          action={canManage ? { label: "Add fee / obligation", onClick: openAdd } : undefined}
        />
      ) : (
        <Table>
          <THead>
            <TR>
              <TH>Type</TH>
              <TH className="text-right">Amount</TH>
              <TH>Payer</TH>
              <TH>Channel</TH>
              <TH>Pool</TH>
              <TH>Status</TH>
              <TH>Incurred</TH>
              {canManage && <TH className="text-right">Actions</TH>}
            </TR>
          </THead>
          <TBody>
            {fees.map((fee) => (
              <TR key={fee.id} data-testid={`fee-row-${fee.id}`}>
                <TD>{fee.feeType}</TD>
                <TD className="text-right tabular-nums">{formatCurrency(fee.amount)}</TD>
                <TD>{fee.payer}</TD>
                <TD className="text-gray-mid">
                  {CHANNEL_LABEL[fee.paymentChannel] ?? `Channel ${fee.paymentChannel}`}
                </TD>
                <TD>{fee.revenuePoolCode ?? "—"}</TD>
                <TD>
                  <StatusBadge tone={STATUS_TONE[fee.status]}>
                    {STATUS_LABEL[fee.status] ?? fee.status}
                  </StatusBadge>
                </TD>
                <TD className="text-gray-mid whitespace-nowrap">
                  {formatCalendarDate(fee.incurredDate)}
                </TD>
                {canManage && (
                  <TD className="text-right whitespace-nowrap">
                    <div className="inline-flex items-center gap-space-2 justify-end">
                      {/* Status actions apply to record-only tracker rows
                          (sourceType null); pipeline rows are status-managed
                          by B3 approve/waive + statement billing. */}
                      {!fee.sourceType && fee.status === "pending" && (
                        <RowAction
                          label="Mark billed"
                          disabled={update.isPending}
                          onClick={() => setStatus(fee, "billed")}
                          testId={`fee-bill-${fee.id}`}
                        />
                      )}
                      {!fee.sourceType &&
                        (fee.status === "pending" || fee.status === "billed") && (
                          <RowAction
                            label="Mark collected"
                            disabled={update.isPending}
                            onClick={() => setStatus(fee, "collected")}
                            testId={`fee-collect-${fee.id}`}
                          />
                        )}
                      <RowAction
                        label="Edit"
                        onClick={() => openEdit(fee)}
                        testId={`fee-edit-${fee.id}`}
                      />
                    </div>
                  </TD>
                )}
              </TR>
            ))}
          </TBody>
        </Table>
      )}

      {/* Conditionally mounted so each open starts with fresh wizard state. */}
      {wizardOpen && (
        <AddObligationWizard
          loanId={loanId}
          open
          onClose={() => setWizardOpen(false)}
        />
      )}

      {/* Edit-only since PR-2 — creation goes through the wizard. */}
      <LoanFeeEntryModal
        loanId={loanId}
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        fee={editing}
      />
    </Card>
  );
}

function SummaryTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border-light bg-white-warm px-space-3 py-space-2">
      <div className="text-caption uppercase tracking-label text-gray-mid">{label}</div>
      <div className="text-h3 font-display font-bold text-onyx tabular-nums">{value}</div>
    </div>
  );
}

function RowAction({
  label,
  onClick,
  disabled,
  testId,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  testId: string;
}) {
  return (
    <button
      type="button"
      className="text-small text-gold-shadow hover:underline disabled:opacity-40 disabled:pointer-events-none"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
    >
      {label}
    </button>
  );
}
